import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './SpeedoTubeUploadModal.css';
import {
  MdClose,
  MdCloudUpload,
  MdOutlineVideocam,
  MdOutlineSlowMotionVideo,
  MdCheckCircle,
  MdErrorOutline,
  MdWarning,
  MdOutlineSubtitles,
  MdOutlineAudiotrack,
  MdOutlineBookmarks,
  MdOutlineSchedule,
  MdOutlineVisibility,
  MdOutlineLock,
  MdOutlinePublic,
  MdOutlineLink,
  MdOutlineHelpOutline,
  MdChevronRight,
  MdExpandMore,
  MdAdd,
  MdDeleteOutline,
  MdOutlineEdit,
  MdPlayArrow,
  MdPause,
  MdRefresh,
  MdOutlineCheckCircle,
  MdInfoOutline,
  MdOutlineAlbum,
  MdAlbum,
  MdMusicNote,
  MdGraphicEq,
  MdArrowUpward,
  MdArrowDownward,
  MdCropLandscape,
  MdChildCare,
  MdFamilyRestroom,
  MdOutlineExplicit,
} from 'react-icons/md';
import { uploadChannelAlbum } from './api/studioApi';
import { getActiveAccount } from './utils/accountUtils';
import logoPng from './assets/speedotube_logo_2ddark.png';
import logoLightPng from './assets/speedotube_logo_2dlight.png';

// 18+ Content Rating Icon (Square badge with 18+ inside replacing [E])
function Icon18Plus({ size = 22, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0 }}
    >
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" />
      <path d="M7 15.5V8.5" />
      <path d="M10 10.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0" />
      <path d="M10 13.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0" />
      <path d="M15.5 12h3" />
      <path d="M17 10.5v3" />
    </svg>
  );
}

function CustomSelect({ options, value, onChange, placeholder = 'Select...' }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value || opt === value);
  const selectedLabel = typeof selectedOption === 'object' ? selectedOption.label : selectedOption || value;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`spt-custom-select-container ${isOpen ? 'is-open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="spt-custom-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="spt-custom-select-value">{selectedLabel || placeholder}</span>
        <MdExpandMore className={`spt-select-chevron ${isOpen ? 'open' : ''}`} size={20} />
      </button>

      <div className={`spt-custom-select-menu ${isOpen ? 'open' : ''}`}>
        {options.map((opt) => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          const optLabel = typeof opt === 'object' ? opt.label : opt;
          const isSelected = optValue === value;
          return (
            <div
              key={optValue}
              className={`spt-custom-select-item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onChange(optValue);
                setIsOpen(false);
              }}
            >
              <span>{optLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CustomDatePicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const dateObj = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewDate, setViewDate] = useState(dateObj);

  useEffect(() => {
    if (value) {
      setViewDate(new Date(value + 'T00:00:00'));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setViewDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    setViewDate(new Date(year, month + 1, 1));
  };

  const handleSelectDay = (day) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    onChange(`${year}-${mm}-${dd}`);
    setIsOpen(false);
  };

  const formattedDisplay = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className={`spt-custom-picker-container ${isOpen ? 'is-open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="spt-custom-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="spt-picker-val-text">{formattedDisplay}</span>
        <MdOutlineSchedule className="spt-picker-icon" size={18} />
      </button>

      <div className={`spt-custom-calendar-popover ${isOpen ? 'open' : ''}`}>
        <div className="spt-cal-header">
          <button type="button" className="spt-cal-nav-btn" onClick={handlePrevMonth}>‹</button>
          <span className="spt-cal-title">{monthNames[month]} {year}</span>
          <button type="button" className="spt-cal-nav-btn" onClick={handleNextMonth}>›</button>
        </div>

        <div className="spt-cal-weekdays">
          <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
        </div>

        <div className="spt-cal-days-grid">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <span key={`empty-${i}`} className="spt-cal-day empty" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const isSelected =
              dateObj.getFullYear() === year &&
              dateObj.getMonth() === month &&
              dateObj.getDate() === day;

            return (
              <button
                key={`day-${day}`}
                type="button"
                className={`spt-cal-day ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectDay(day)}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CustomTimePicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const timeVal = `${hh}:${mm}`;

      const period = h >= 12 ? 'PM' : 'AM';
      const displayHour = h % 12 === 0 ? 12 : h % 12;
      const displayTime = `${String(displayHour).padStart(2, '0')}:${mm} ${period}`;

      times.push({ value: timeVal, label: displayTime });
    }
  }

  const selectedTimeObj = times.find((t) => t.value === value) || { value, label: value };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`spt-custom-picker-container ${isOpen ? 'is-open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="spt-custom-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="spt-picker-val-text">{selectedTimeObj.label}</span>
        <MdExpandMore className={`spt-select-chevron ${isOpen ? 'open' : ''}`} size={18} />
      </button>

      <div className={`spt-custom-time-popover ${isOpen ? 'open' : ''}`}>
        {times.map((item) => {
          const isSelected = item.value === value;
          return (
            <div
              key={item.value}
              className={`spt-time-item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onChange(item.value);
                setIsOpen(false);
              }}
            >
              <span>{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlbumUploadWorkflow({
  albumStep,
  setAlbumStep,
  albumTitle,
  setAlbumTitle,
  albumArtist,
  setAlbumArtist,
  albumGenre,
  setAlbumGenre,
  albumReleaseYear,
  setAlbumReleaseYear,
  albumDescription,
  setAlbumDescription,
  albumCoverUrl,
  albumCoverInputRef,
  handleCoverArtChange,
  albumPlayboxBannerUrl,
  albumPlayboxBannerInputRef,
  handlePlayboxBannerChange,
  albumTracks,
  albumTrackInputRef,
  handleAddAudioTracks,
  handleRemoveTrack,
  handleMoveTrack,
  handleTrackTitleChange,
  playingTrackId,
  togglePlayTrack,
  trackCurrentTime = 0,
  trackDuration = 0,
  handleSeekTrack,
  albumVisibility,
  setAlbumVisibility,
  albumAdvisory,
  setAlbumAdvisory,
  transitionDirection = 'forward',
}) {
  const formatSecs = (totalSec) => {
    if (!totalSec || isNaN(totalSec)) return '0:00';
    const m = Math.floor(totalSec / 60);
    const s = Math.floor(totalSec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const activeUser = getActiveAccount() || {};
  const currentCreatorName = activeUser.name || activeUser.fullName || 'Creator';
  const currentCreatorHandle = activeUser.handle || (activeUser.name ? `@${activeUser.name.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : '@creator');

  return (
    <div style={{ width: '100%' }}>
      <div
        key={albumStep}
        className={`spt-step-pane-anim spt-anim-${transitionDirection}`}
      >
        {albumStep === 1 && (
          <div className="spt-album-grid-layout">
          <div className="spt-album-form-col">
            <div className="spt-album-form-group">
              <label className="spt-album-form-label">Album Title *</label>
              <input
                type="text"
                className="spt-album-input"
                placeholder="e.g. Echoes of Eternity (Official Studio Album)"
                value={albumTitle}
                onChange={(e) => setAlbumTitle(e.target.value)}
              />
            </div>

            <div className="spt-album-form-row">
              <div className="spt-album-form-group">
                <label className="spt-album-form-label">Artist / Band Name *</label>
                <input
                  type="text"
                  className="spt-album-input"
                  placeholder={`e.g. ${currentCreatorName}`}
                  value={albumArtist}
                  onChange={(e) => setAlbumArtist(e.target.value)}
                />
              </div>
              <div className="spt-album-form-group">
                <label className="spt-album-form-label">Genre</label>
                <CustomSelect
                  options={['Pop', 'Dance', 'Rock', 'Hip Hop', 'Electronic', 'R&B', 'Acoustic', 'Lo-Fi', 'Classical', 'Jazz']}
                  value={albumGenre}
                  onChange={(val) => setAlbumGenre(val)}
                />
              </div>
            </div>

            <div className="spt-album-form-row">
              <div className="spt-album-form-group">
                <label className="spt-album-form-label">Release Year</label>
                <input
                  type="text"
                  className="spt-album-input"
                  placeholder={new Date().getFullYear().toString()}
                  value={albumReleaseYear}
                  onChange={(e) => setAlbumReleaseYear(e.target.value)}
                />
              </div>
              <div className="spt-album-form-group">
                <label className="spt-album-form-label">Creator Channel</label>
                <input
                  type="text"
                  className="spt-album-input"
                  disabled
                  value={`${currentCreatorName} (${currentCreatorHandle})`}
                  style={{ opacity: 0.7, cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div className="spt-album-form-group">
              <label className="spt-album-form-label">Album Description & Liner Notes</label>
              <textarea
                className="spt-album-input"
                rows={4}
                placeholder="Share the story behind this album, featured musicians, production credits, and inspiration..."
                value={albumDescription}
                onChange={(e) => setAlbumDescription(e.target.value)}
              />
            </div>
          </div>

          {/* Right Column: Two Banner Upload Options */}
          <div className="spt-album-cover-col">
            {/* Banner 1: Thumbnail Cover (Square 1:1) */}
            <div className="spt-banner-card-box spt-banner-square-box">
              <div className="spt-banner-header-row">
                <label className="spt-album-form-label">Album Banner (Square 1:1)</label>
              </div>
              <div
                className={`spt-album-cover-dropzone spt-album-square-dropzone ${albumCoverUrl ? 'has-cover' : ''}`}
                onClick={() => albumCoverInputRef.current && albumCoverInputRef.current.click()}
              >
                {albumCoverUrl ? (
                  <>
                    <img
                      src={albumCoverUrl}
                      alt="Thumbnail Cover (Square 1:1)"
                      className="spt-album-cover-preview-img spt-square-preview-img"
                    />
                    <div className="spt-banner-aspect-tag">1:1 SQUARE</div>
                    <button
                      type="button"
                      className="spt-cover-change-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        albumCoverInputRef.current && albumCoverInputRef.current.click();
                      }}
                    >
                      Change Cover
                    </button>
                  </>
                ) : (
                  <>
                    <MdOutlineAlbum size={44} className="spt-album-dropzone-icon" />
                    <strong className="spt-album-dropzone-title">
                      Upload Album Banner
                    </strong>
                    <span className="spt-album-dropzone-subtext">
                      Square 1:1 ratio banner image (PNG, JPG, WEBP)
                    </span>
                  </>
                )}
              </div>
              <input
                type="file"
                ref={albumCoverInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files && handleCoverArtChange(e.target.files[0])}
              />
              <p className="spt-banner-instruction-subtext spt-banner-square-hint">
                (This one will be displayed on the Thumbnail Cover & Album Player)
              </p>
            </div>

            {/* Banner 2: Video Playbox Banner (16:9 Landscape) */}
            <div className="spt-banner-card-box">
              <div className="spt-banner-header-row">
                <label className="spt-album-form-label">Video Playbox Banner (16:9 Landscape)</label>
              </div>
              <div
                className={`spt-album-cover-dropzone spt-album-playbox-dropzone ${albumPlayboxBannerUrl ? 'has-cover' : ''}`}
                onClick={() => albumPlayboxBannerInputRef.current && albumPlayboxBannerInputRef.current.click()}
              >
                {albumPlayboxBannerUrl ? (
                  <>
                    <img
                      src={albumPlayboxBannerUrl}
                      alt="Video Playbox Banner (16:9 Landscape)"
                      className="spt-album-cover-preview-img spt-playbox-preview-img"
                    />
                    <div className="spt-banner-aspect-tag">16:9 LANDSCAPE</div>
                    <button
                      type="button"
                      className="spt-cover-change-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        albumPlayboxBannerInputRef.current && albumPlayboxBannerInputRef.current.click();
                      }}
                    >
                      Change Banner
                    </button>
                  </>
                ) : (
                  <>
                    <MdCropLandscape size={44} className="spt-album-dropzone-icon" />
                    <strong className="spt-album-dropzone-title">
                      Upload Playbox Banner
                    </strong>
                    <span className="spt-album-dropzone-subtext">
                      16:9 widescreen banner image (PNG, JPG, WEBP)
                    </span>
                  </>
                )}
              </div>
              <input
                type="file"
                ref={albumPlayboxBannerInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files && handlePlayboxBannerChange(e.target.files[0])}
              />
              <p className="spt-banner-instruction-subtext">
                (This one would be Display in the video Playbox)
              </p>
            </div>
          </div>
        </div>
      )}

      {albumStep === 2 && (
        <div className="spt-tracklist-view">
          <div
            className="spt-tracklist-dropzone"
            onClick={() => albumTrackInputRef.current && albumTrackInputRef.current.click()}
          >
            <MdMusicNote size={36} className="spt-album-dropzone-icon" />
            <h4 className="spt-album-step-heading">
              Add Audio Tracks to Album
            </h4>
            <p className="spt-album-step-subtext">
              Click or drag & drop audio files (MP3, WAV, AAC, FLAC, M4A). Select multiple tracks at once!
            </p>
          </div>
          <input
            type="file"
            ref={albumTrackInputRef}
            accept="audio/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => e.target.files && handleAddAudioTracks(e.target.files)}
          />

          {albumTracks.length > 0 ? (
            <div className="spt-tracklist-table">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span className="spt-album-step-heading" style={{ fontSize: '14px', fontWeight: '700' }}>
                  Tracklist ({albumTracks.length} {albumTracks.length === 1 ? 'Track' : 'Tracks'})
                </span>
                <button
                  type="button"
                  className="spt-primary-pill-btn"
                  style={{ padding: '6px 14px', fontSize: '12px' }}
                  onClick={() => albumTrackInputRef.current && albumTrackInputRef.current.click()}
                >
                  <MdAdd size={16} /> Add Tracks
                </button>
              </div>

              {albumTracks.map((t, index) => {
                const isPlaying = playingTrackId === t.id;
                const progressPct =
                  isPlaying && trackDuration > 0
                    ? (trackCurrentTime / trackDuration) * 100
                    : 0;
                return (
                  <div
                    key={t.id}
                    className={`spt-track-item-card ${isPlaying ? 'is-playing' : ''}`}
                  >
                    <div className="spt-track-left-group">
                      <span className="spt-track-num-badge">{t.trackNumber}</span>
                      <button
                        type="button"
                        className="spt-track-play-preview-btn"
                        onClick={() => togglePlayTrack(t)}
                        title={isPlaying ? 'Pause' : 'Preview Track'}
                      >
                        {isPlaying ? <MdPause size={18} /> : <MdPlayArrow size={18} />}
                      </button>
                      <input
                        type="text"
                        className="spt-track-title-input"
                        value={t.title}
                        onChange={(e) => handleTrackTitleChange(t.id, e.target.value)}
                        placeholder={`Track ${index + 1}`}
                      />
                      {/* Animated Sound Beat Equalizer Bars while playing */}
                      {isPlaying && (
                        <div className="spt-beat-equalizer-bars" title="Audio playing">
                          <span className="spt-beat-eq-bar bar1" />
                          <span className="spt-beat-eq-bar bar2" />
                          <span className="spt-beat-eq-bar bar3" />
                          <span className="spt-beat-eq-bar bar4" />
                          <span className="spt-beat-eq-bar bar5" />
                          <span className="spt-beat-eq-bar bar6" />
                        </div>
                      )}
                    </div>

                    <span className="spt-track-duration-pill">
                      {isPlaying
                        ? `${formatSecs(trackCurrentTime)} / ${t.duration || '0:00'}`
                        : (t.duration || '0:00')}
                    </span>

                    <div className="spt-track-actions">
                      <button
                        type="button"
                        className="spt-track-action-btn"
                        disabled={index === 0}
                        onClick={() => handleMoveTrack(index, 'up')}
                        title="Move Up"
                      >
                        <MdArrowUpward size={18} />
                      </button>
                      <button
                        type="button"
                        className="spt-track-action-btn"
                        disabled={index === albumTracks.length - 1}
                        onClick={() => handleMoveTrack(index, 'down')}
                        title="Move Down"
                      >
                        <MdArrowDownward size={18} />
                      </button>
                      <button
                        type="button"
                        className="spt-track-action-btn delete"
                        onClick={() => handleRemoveTrack(t.id)}
                        title="Remove Track"
                      >
                        <MdDeleteOutline size={18} />
                      </button>
                    </div>

                    {/* Bottom Beat Glow Line */}
                    {isPlaying && (
                      <div
                        className="spt-track-bottom-beat-line"
                        style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="spt-album-empty-tracks">
              <MdGraphicEq size={40} style={{ opacity: 0.5, marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>
                No tracks added yet. Upload at least one audio song file to continue.
              </p>
            </div>
          )}
        </div>
      )}

      {albumStep === 3 && (
        <div className="spt-album-vis-view">
          <h4 className="spt-album-step-heading">
            Album Distribution & Visibility
          </h4>
          <div
            className={`spt-album-vis-card ${albumVisibility === 'PUBLIC' ? 'active' : ''}`}
            onClick={() => setAlbumVisibility('PUBLIC')}
          >
            <MdOutlinePublic size={24} className="spt-album-vis-icon" />
            <div>
              <strong className="spt-album-card-title">Public</strong>
              <p className="spt-album-card-desc">
                Everyone can listen to and discover this album on SpeedoTube and SpeedoMusic.
              </p>
            </div>
          </div>

          <div
            className={`spt-album-vis-card ${albumVisibility === 'UNLISTED' ? 'active' : ''}`}
            onClick={() => setAlbumVisibility('UNLISTED')}
          >
            <MdOutlineLink size={24} className="spt-album-vis-icon" />
            <div>
              <strong className="spt-album-card-title">Unlisted</strong>
              <p className="spt-album-card-desc">
                Anyone with the album link can listen. It will not appear in search or recommendations.
              </p>
            </div>
          </div>

          <div
            className={`spt-album-vis-card ${albumVisibility === 'PRIVATE' ? 'active' : ''}`}
            onClick={() => setAlbumVisibility('PRIVATE')}
          >
            <MdOutlineLock size={24} className="spt-album-vis-icon" />
            <div>
              <strong className="spt-album-card-title">Private</strong>
              <p className="spt-album-card-desc">
                Only you can listen and view this album in Creator Studio.
              </p>
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            <label className="spt-album-form-label" style={{ marginBottom: '8px', display: 'block' }}>
              Content Advisory
            </label>
            <div className="spt-audience-cards-grid">
              <div
                className={`spt-album-vis-card spt-audience-card ${albumAdvisory === 'Kids' ? 'active' : ''}`}
                onClick={() => setAlbumAdvisory('Kids')}
              >
                <MdChildCare size={22} className="spt-album-vis-icon" />
                <div>
                  <strong className="spt-album-card-title">Kids Friendly</strong>
                  <p className="spt-album-card-desc">Designed specifically for children</p>
                </div>
              </div>
              <div
                className={`spt-album-vis-card spt-audience-card ${albumAdvisory === 'Clean' ? 'active' : ''}`}
                onClick={() => setAlbumAdvisory('Clean')}
              >
                <MdFamilyRestroom size={22} className="spt-album-vis-icon" />
                <div>
                  <strong className="spt-album-card-title">Clean / General Audience</strong>
                  <p className="spt-album-card-desc">Suitable for kids as well as other listeners</p>
                </div>
              </div>
              <div
                className={`spt-album-vis-card spt-audience-card ${albumAdvisory === 'Explicit' ? 'active' : ''}`}
                onClick={() => setAlbumAdvisory('Explicit')}
              >
                <Icon18Plus size={22} className="spt-album-vis-icon" />
                <div>
                  <strong className="spt-album-card-title">Explicit Content</strong>
                  <p className="spt-album-card-desc">Contains mature language, themes, or lyrics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {albumStep === 4 && (
        <div className="spt-album-review-view">
          <div className="spt-album-showcase-card">
            <img
              src={albumCoverUrl || (process.env.PUBLIC_URL || '') + '/img/apt_thumb.jpg'}
              alt={albumTitle}
              className="spt-showcase-cover"
              onError={(e) => {
                e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
              }}
            />
            <div className="spt-showcase-info">
              <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#f43f5e', fontWeight: '700' }}>
                Official Music Album &bull; Square Banner (1:1)
              </span>
              <h3 className="spt-showcase-title">{albumTitle || 'Untitled Album'}</h3>
              <div className="spt-showcase-artist">{albumArtist || currentCreatorName}</div>
              <div className="spt-showcase-meta">
                <span>{albumGenre}</span>
                <span>•</span>
                <span>{albumReleaseYear}</span>
                <span>•</span>
                <span>{albumTracks.length} Songs</span>
                <span>•</span>
                <span style={{ color: '#16a34a', fontWeight: '600' }}>Ready to Publish</span>
              </div>
            </div>
          </div>

          <div className="spt-album-review-tracks-card">
            <h4 className="spt-album-step-heading" style={{ margin: '0 0 12px 0', fontSize: '15px' }}>Included Tracks</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {albumTracks.map((t) => (
                <div
                  key={t.id}
                  className="spt-album-review-track-row"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="spt-album-review-track-num">{t.trackNumber}</span>
                    <span className="spt-album-review-track-title">{t.title}</span>
                  </div>
                  <span className="spt-album-review-track-dur">{t.duration}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default function SpeedoTubeUploadModal({
  isOpen,
  onClose,
  onPublishSuccess,
  initialVideoType = null, // 'video' | 'short' | null
  editingItem = null, // when editing an existing video/short from Studio
}) {
  // Stepper State (1: Upload, 2: Details, 3: Elements, 4: Checks, 5: Visibility)
  const [currentStep, setCurrentStep] = useState(1);
  const [isDraftSaved, setIsDraftSaved] = useState(true);

  // Section Selector: 'video' | 'album'
  const [uploadSection, setUploadSection] = useState(() => (initialVideoType === 'album' ? 'album' : 'video'));

  const activeUser = getActiveAccount() || {};
  const currentCreatorName = activeUser.name || activeUser.fullName || 'Creator';
  const currentCreatorHandle = activeUser.handle || (activeUser.name ? `@${activeUser.name.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : '@creator');

  // Album-Specific State
  const [albumStep, setAlbumStep] = useState(1);
  const [albumTitle, setAlbumTitle] = useState('');
  const [albumArtist, setAlbumArtist] = useState(() => currentCreatorName);
  const [albumGenre, setAlbumGenre] = useState('Pop');
  const [albumReleaseYear, setAlbumReleaseYear] = useState(() => new Date().getFullYear().toString());
  const [albumDescription, setAlbumDescription] = useState('');
  const [albumCoverUrl, setAlbumCoverUrl] = useState(null);
  const [albumPlayboxBannerUrl, setAlbumPlayboxBannerUrl] = useState(null);
  const [albumTracks, setAlbumTracks] = useState([]);
  const [albumVisibility, setAlbumVisibility] = useState('PUBLIC');
  const [albumAdvisory, setAlbumAdvisory] = useState('Clean');
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [trackCurrentTime, setTrackCurrentTime] = useState(0);
  const [trackDuration, setTrackDuration] = useState(0);
  const albumAudioRef = useRef(null);
  const albumCoverInputRef = useRef(null);
  const albumPlayboxBannerInputRef = useRef(null);
  const albumTrackInputRef = useRef(null);

  // Buttery Smooth Transition State ('forward' | 'backward' | 'fade')
  const [transitionDirection, setTransitionDirection] = useState('forward');

  const navigateAlbumStep = (newStep) => {
    if (newStep === albumStep) return;
    setTransitionDirection(newStep > albumStep ? 'forward' : 'backward');
    setAlbumStep(newStep);
  };

  const navigateVideoStep = (newStep) => {
    if (newStep === currentStep) return;
    setTransitionDirection(newStep > currentStep ? 'forward' : 'backward');
    setCurrentStep(newStep);
  };

  const switchSection = (section, vType = null) => {
    if (section === uploadSection && (!vType || vType === detectedType)) return;
    setTransitionDirection('fade');
    setUploadSection(section);
    if (vType) setDetectedType(vType);

    if (section === 'album') {
      const storedAlbumDraft = localStorage.getItem('speedotube_draft_album');
      if (storedAlbumDraft && !albumTitle.trim() && albumTracks.length === 0 && !albumCoverUrl) {
        try {
          const parsed = JSON.parse(storedAlbumDraft);
          setSavedAlbumDraftData(parsed);
          setShowDraftPrompt(true);
        } catch (e) {
          localStorage.removeItem('speedotube_draft_album');
        }
      }
    } else if (section === 'video') {
      const storedVideoDraft = localStorage.getItem('speedotube_draft_video');
      if (storedVideoDraft && !selectedFile) {
        try {
          const parsed = JSON.parse(storedVideoDraft);
          setSavedDraftData(parsed);
          setShowDraftPrompt(true);
        } catch (e) {
          localStorage.removeItem('speedotube_draft_video');
        }
      }
    }
  };

  useEffect(() => {
    if (initialVideoType === 'album') {
      setUploadSection('album');
    } else if (initialVideoType === 'short') {
      setUploadSection('video');
      setDetectedType('short');
    } else if (initialVideoType === 'video') {
      setUploadSection('video');
      setDetectedType('video');
    }
  }, [initialVideoType]);

  // Smooth Closing & Mount Animation State
  const [isClosing, setIsClosing] = useState(false);
  const [isRendered, setIsRendered] = useState(isOpen);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [savedDraftData, setSavedDraftData] = useState(null);
  const [savedAlbumDraftData, setSavedAlbumDraftData] = useState(null);

  // Upload File & Media Metadata State
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [detectedType, setDetectedType] = useState('video'); // 'video' | 'short'
  const [isManualTypeOverride, setIsManualTypeOverride] = useState(false);
  const [isTechOpen, setIsTechOpen] = useState(false);

  // Upload Progress & Processing State
  const [uploadState, setUploadState] = useState('idle'); // 'idle' | 'uploading' | 'paused' | 'uploaded' | 'processing' | 'ready' | 'failed'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState('18.4 MB/s');
  const [uploadedBytesFormatted, setUploadedBytesFormatted] = useState('0 MB');
  const [totalBytesFormatted, setTotalBytesFormatted] = useState('0 MB');
  const [timeRemaining, setTimeRemaining] = useState('Calculating...');
  
  // Processing Checklist Stages
  const [processingStages, setProcessingStages] = useState({
    upload: 'pending',
    analyzed: 'pending',
    qualities: 'pending',
    audio: 'pending',
    thumbnails: 'pending',
    streaming: 'pending',
    checks: 'pending',
  });

  const [renditions, setRenditions] = useState([]);

  // Step 2: Details Metadata State
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const [customThumbnailUrl, setCustomThumbnailUrl] = useState(null);
  const [autoThumbnails, setAutoThumbnails] = useState([]);
  const [playlist, setPlaylist] = useState('None');
  const [isForKids, setIsForKids] = useState('all'); // 'yes' | 'all' | 'no'
  const [category, setCategory] = useState('Gaming');
  const [language, setLanguage] = useState('English');
  const [tagsInput, setTagsInput] = useState('speedotube, 4k, gaming, stream');
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);

  // Short Specific Details
  const [shortHashtags, setShortHashtags] = useState('#Shorts #SpeedoTube #Viral');

  // Advanced Options State
  const [recordingDate, setRecordingDate] = useState(new Date().toISOString().split('T')[0]);
  const [recordingLocation, setRecordingLocation] = useState('');
  const [license, setLicense] = useState('SpeedoTube Standard License');
  const [allowEmbedding, setAllowEmbedding] = useState(true);
  const [allowComments, setAllowComments] = useState('all');
  const [showLikeCount, setShowLikeCount] = useState(true);
  const [ageRestriction, setAgeRestriction] = useState(false);
  const [paidPromotion, setPaidPromotion] = useState(false);

  // Step 3: Elements State
  const [subtitleTracks, setSubtitleTracks] = useState([]);
  const [audioTracksList, setAudioTracksList] = useState([
    { id: 1, lang: 'English', name: 'Original Audio (Default)', status: 'Active' },
  ]);
  const [chapters, setChapters] = useState([]);

  // Step 4: Checks State
  const [checksStatus, setChecksStatus] = useState({
    copyright: 'scanning', // 'scanning' | 'passed' | 'warning'
    contentSafety: 'scanning', // 'scanning' | 'passed' | 'warning'
    technical: 'passed',
  });

  // Step 5: Visibility & Scheduling State
  const [visibility, setVisibility] = useState('public'); // 'public' | 'unlisted' | 'private' | 'schedule'
  const [scheduleDate, setScheduleDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const [scheduleTime, setScheduleTime] = useState('07:30');
  const [scheduleTimezone, setScheduleTimezone] = useState('IST (UTC+5:30)');

  // Drag & Drop Active state
  const [isDragOver, setIsDragOver] = useState(false);

  // File Input & Timer Refs
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const uploadTimerRef = useRef(null);
  const processingTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setIsClosing(false);
    } else if (isRendered) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsRendered(false);
        setIsClosing(false);
      }, 220);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const resetModalState = () => {
    if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
    if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl(null);
    }
    if (customThumbnailUrl) {
      URL.revokeObjectURL(customThumbnailUrl);
      setCustomThumbnailUrl(null);
    }
    setSelectedThumbnail(0);
    setVideoDescription('');
    setVideoTitle('');
    setSelectedFile(null);
    setFileMetadata(null);
    setUploadState('idle');
    setUploadProgress(0);
    setCurrentStep(1);
    setShowCancelConfirm(false);
    setShowDraftPrompt(false);
  };

  const resetAlbumState = () => {
    setAlbumStep(1);
    setAlbumTitle('');
    setAlbumArtist(currentCreatorName);
    setAlbumGenre('Pop');
    setAlbumReleaseYear(new Date().getFullYear().toString());
    setAlbumDescription('');
    setAlbumCoverUrl(null);
    setAlbumPlayboxBannerUrl(null);
    setAlbumTracks([]);
    setAlbumVisibility('PUBLIC');
    setAlbumAdvisory('Clean');
  };

  const executeClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      resetModalState();
    }, 200);
  };

  const saveCurrentDraftToLocalStorage = () => {
    if (!selectedFile) return;
    const draftObj = {
      fileName: selectedFile.name || 'speedo_video.mp4',
      videoTitle,
      videoDescription,
      detectedType,
      fileMetadata,
      currentStep,
      category,
      isForKids,
      language,
      tagsInput,
      visibility,
      scheduleDate,
      scheduleTime,
      scheduleTimezone,
      savedAt: Date.now(),
    };
    localStorage.setItem('speedotube_draft_video', JSON.stringify(draftObj));
  };

  const saveCurrentAlbumDraftToLocalStorage = () => {
    const draftObj = {
      albumTitle: albumTitle || 'Untitled Album',
      albumArtist: albumArtist || currentCreatorName,
      genre: albumGenre || 'Pop',
      releaseYear: albumReleaseYear || new Date().getFullYear().toString(),
      albumDescription,
      albumCoverUrl,
      albumPlayboxBannerUrl,
      albumTracks,
      albumVisibility,
      albumAdvisory,
      albumStep,
      savedAt: Date.now(),
    };
    try {
      localStorage.setItem('speedotube_draft_album', JSON.stringify(draftObj));
      setIsDraftSaved(true);
    } catch (e) {
      console.warn('Failed to save album draft to localStorage', e);
    }
  };

  const handleSmoothClose = () => {
    if (editingItem) {
      executeClose();
      return;
    }
    if (uploadSection === 'album') {
      saveCurrentAlbumDraftToLocalStorage();
      setShowCancelConfirm(true);
      return;
    }
    if (selectedFile) {
      saveCurrentDraftToLocalStorage();
      setShowCancelConfirm(true);
      return;
    }
    executeClose();
  };

  const handleSaveDraftAndClose = () => {
    if (uploadSection === 'album') {
      saveCurrentAlbumDraftToLocalStorage();
    } else {
      saveCurrentDraftToLocalStorage();
    }
    executeClose();
  };

  const handleDiscardDraftAndClose = () => {
    if (uploadSection === 'album') {
      localStorage.removeItem('speedotube_draft_album');
      setSavedAlbumDraftData(null);
      resetAlbumState();
    } else {
      localStorage.removeItem('speedotube_draft_video');
      setSavedDraftData(null);
    }
    executeClose();
  };

  // Sync initialVideoType & editingItem props with detectedType state and check for existing Draft when modal opens
  useEffect(() => {
    if (isOpen) {
      resetModalState();

      if (editingItem) {
        setUploadSection('video');
        const isShort = Boolean(editingItem.isShort);
        setDetectedType(isShort ? 'short' : 'video');
        setIsManualTypeOverride(true);
        setCurrentStep(2);
        setVideoTitle(editingItem.title || '');
        setVideoDescription(editingItem.description || '');
        setVisibility((editingItem.visibility || 'public').toLowerCase());
        setCategory(editingItem.category || 'Gaming');
        setTagsInput(editingItem.tags || '');
        if (editingItem.thumbnailUrl) {
          setCustomThumbnailUrl(editingItem.thumbnailUrl);
          setSelectedThumbnail(99);
        } else {
          setCustomThumbnailUrl(null);
          setSelectedThumbnail(0);
        }
        setVideoPreviewUrl(editingItem.videoUrl || editingItem.streamUrl || null);
        setFileMetadata({
          name: editingItem.title ? `${editingItem.title}.mp4` : 'video.mp4',
          size: editingItem.size || '38.4 MB',
          duration: editingItem.duration || '02:30',
          qualityBadge: '1080p Full HD',
          aspectRatio: isShort ? '9:16' : '16:9',
          isShort,
          fps: 60,
          videoCodec: 'H.264 / AVC',
          audioCodec: 'AAC-LC (48 kHz)',
          bitrate: '9.5 Mbps',
          hdr: 'SDR',
          colorSpace: 'BT.709',
        });
        setUploadState('ready');
        setUploadProgress(100);
        setUploadedBytesFormatted('38.4 MB');
        setTotalBytesFormatted('38.4 MB');
        setProcessingStages({
          upload: 'completed',
          analyzed: 'completed',
          qualities: 'completed',
          audio: 'completed',
          thumbnails: 'completed',
          streaming: 'completed',
          checks: 'completed',
        });
        setChecksStatus({ copyright: 'passed', contentSafety: 'passed', technical: 'passed' });
        setIsDraftSaved(true);
        setShowDraftPrompt(false);
        return;
      }

      if (uploadSection === 'album' || initialVideoType === 'album') {
        const storedAlbumDraft = localStorage.getItem('speedotube_draft_album');
        if (storedAlbumDraft) {
          try {
            const parsedDraft = JSON.parse(storedAlbumDraft);
            setSavedAlbumDraftData(parsedDraft);
            setShowDraftPrompt(true);
          } catch (e) {
            localStorage.removeItem('speedotube_draft_album');
          }
        }
      } else {
        const storedDraft = localStorage.getItem('speedotube_draft_video');
        if (storedDraft) {
          try {
            const parsedDraft = JSON.parse(storedDraft);
            setSavedDraftData(parsedDraft);
            setShowDraftPrompt(true);
          } catch (e) {
            localStorage.removeItem('speedotube_draft_video');
          }
        }
      }

      const isShortType = initialVideoType === 'short';
      if (isShortType) {
        setDetectedType('short');
        setIsManualTypeOverride(true);
      } else if (initialVideoType === 'video') {
        setDetectedType('video');
        setIsManualTypeOverride(true);
      }
    }
  }, [initialVideoType, isOpen, uploadSection, editingItem]);

  // Save Draft to LocalStorage whenever media/metadata/fields change
  useEffect(() => {
    if (selectedFile && isOpen && !editingItem) {
      const draftObj = {
        fileName: selectedFile.name || 'speedo_video.mp4',
        videoTitle,
        videoDescription,
        detectedType,
        fileMetadata,
        currentStep,
        category,
        isForKids,
        language,
        tagsInput,
        visibility,
        scheduleDate,
        scheduleTime,
        scheduleTimezone,
        savedAt: Date.now(),
      };
      localStorage.setItem('speedotube_draft_video', JSON.stringify(draftObj));
      setIsDraftSaved(true);
    }
  }, [selectedFile, videoTitle, videoDescription, category, isForKids, visibility, currentStep, isOpen, editingItem]);

  // Auto-Save Album Draft to LocalStorage whenever album fields change
  useEffect(() => {
    if (isOpen && uploadSection === 'album') {
      const hasContent = Boolean(
        albumTitle.trim() ||
        albumTracks.length > 0 ||
        albumCoverUrl ||
        albumDescription.trim() ||
        albumStep > 1
      );
      if (hasContent) {
        setIsDraftSaved(false);
        const timer = setTimeout(() => {
          saveCurrentAlbumDraftToLocalStorage();
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [
    albumTitle,
    albumArtist,
    albumGenre,
    albumReleaseYear,
    albumDescription,
    albumCoverUrl,
    albumTracks,
    albumVisibility,
    albumAdvisory,
    albumStep,
    isOpen,
    uploadSection,
  ]);

  // Resume Draft Handler ("Edit Draft Video")
  const handleResumeDraft = () => {
    if (!savedDraftData) return;
    const mockFile = new File([''], savedDraftData.fileName || 'speedo_video.mp4', { type: 'video/mp4' });
    setSelectedFile(mockFile);
    if (savedDraftData.fileMetadata) setFileMetadata(savedDraftData.fileMetadata);
    if (savedDraftData.videoTitle !== undefined) setVideoTitle(savedDraftData.videoTitle);
    if (savedDraftData.videoDescription !== undefined) setVideoDescription(savedDraftData.videoDescription);
    if (savedDraftData.category) setCategory(savedDraftData.category);
    if (savedDraftData.isForKids) setIsForKids(savedDraftData.isForKids);
    if (savedDraftData.detectedType) setDetectedType(savedDraftData.detectedType);
    if (savedDraftData.visibility) setVisibility(savedDraftData.visibility);
    if (savedDraftData.scheduleDate) setScheduleDate(savedDraftData.scheduleDate);
    if (savedDraftData.scheduleTime) setScheduleTime(savedDraftData.scheduleTime);
    if (savedDraftData.scheduleTimezone) setScheduleTimezone(savedDraftData.scheduleTimezone);

    setCurrentStep(savedDraftData.currentStep || 2);
    setUploadState('ready');
    setUploadProgress(100);
    setShowDraftPrompt(false);
  };

  // Resume Album Draft Handler
  const handleResumeAlbumDraft = () => {
    if (!savedAlbumDraftData) return;
    if (savedAlbumDraftData.albumTitle !== undefined) setAlbumTitle(savedAlbumDraftData.albumTitle);
    if (savedAlbumDraftData.albumArtist !== undefined) setAlbumArtist(savedAlbumDraftData.albumArtist);
    if (savedAlbumDraftData.albumGenre !== undefined) setAlbumGenre(savedAlbumDraftData.albumGenre);
    if (savedAlbumDraftData.albumReleaseYear !== undefined) setAlbumReleaseYear(savedAlbumDraftData.albumReleaseYear);
    if (savedAlbumDraftData.albumDescription !== undefined) setAlbumDescription(savedAlbumDraftData.albumDescription);
    if (savedAlbumDraftData.albumCoverUrl !== undefined) setAlbumCoverUrl(savedAlbumDraftData.albumCoverUrl);
    if (savedAlbumDraftData.albumPlayboxBannerUrl !== undefined) setAlbumPlayboxBannerUrl(savedAlbumDraftData.albumPlayboxBannerUrl);
    if (savedAlbumDraftData.albumTracks) setAlbumTracks(savedAlbumDraftData.albumTracks);
    if (savedAlbumDraftData.albumVisibility !== undefined) setAlbumVisibility(savedAlbumDraftData.albumVisibility);
    if (savedAlbumDraftData.albumAdvisory !== undefined) setAlbumAdvisory(savedAlbumDraftData.albumAdvisory);
    if (savedAlbumDraftData.albumStep !== undefined) setAlbumStep(savedAlbumDraftData.albumStep);
    setShowDraftPrompt(false);
    setIsDraftSaved(true);
  };

  // Discard Album Draft Handler
  const handleDiscardAlbumDraftAndStartNew = () => {
    localStorage.removeItem('speedotube_draft_album');
    setSavedAlbumDraftData(null);
    setShowDraftPrompt(false);
    resetAlbumState();
    setIsDraftSaved(true);
  };

  // Discard Draft & Start New Handler ("Upload New Video")
  const handleDiscardDraftAndStartNew = () => {
    localStorage.removeItem('speedotube_draft_video');
    setSavedDraftData(null);
    setShowDraftPrompt(false);
    resetModalState();
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
      if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    };
  }, [videoPreviewUrl]);

  // Auto-set title from filename when selected
  const handleFileChange = (file) => {
    if (!file) return;

    // Reset state & clear custom thumbnail and description
    setSelectedFile(file);
    setVideoDescription('');
    if (customThumbnailUrl) {
      URL.revokeObjectURL(customThumbnailUrl);
      setCustomThumbnailUrl(null);
    }
    setSelectedThumbnail(0);
    const cleanTitle = file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
    setVideoTitle(cleanTitle.slice(0, 100));

    // Create object URL for analysis
    const objectUrl = URL.createObjectURL(file);
    setVideoPreviewUrl(objectUrl);

    // Analyze HTML5 Video File metadata
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = objectUrl;

    tempVideo.onloadedmetadata = () => {
      const width = tempVideo.videoWidth || 1920;
      const height = tempVideo.videoHeight || 1080;
      const durationSec = tempVideo.duration || 120;

      // Calculate Aspect Ratio
      const ratioVal = width / height;
      let aspectRatio = '16:9';
      let isShort = false;

      if (ratioVal < 0.85) {
        aspectRatio = '9:16';
        isShort = true;
      } else if (Math.abs(ratioVal - 1.0) < 0.1) {
        aspectRatio = '1:1';
        isShort = false;
      } else if (ratioVal > 1.2) {
        aspectRatio = '16:9';
        isShort = false;
      }

      if (initialVideoType === 'short') {
        isShort = true;
      }

      const is4K = width >= 3840 || height >= 2160;
      const is1440p = (width >= 2560 && width < 3840) || (height >= 1440 && height < 2160);
      const is1080p = (width >= 1920 && width < 2560) || (height >= 1080 && height < 1440);

      let qualityBadge = '1080p Full HD';
      if (is4K) qualityBadge = '4K Ultra HD';
      else if (is1440p) qualityBadge = '1440p QHD';
      else if (is1080p) qualityBadge = '1080p Full HD';
      else if (width >= 1280) qualityBadge = '720p HD';
      else qualityBadge = '480p SD';

      // Format Duration
      const mins = Math.floor(durationSec / 60);
      const secs = Math.floor(durationSec % 60);
      const formattedDuration = `${mins}:${secs < 10 ? '0' : ''}${secs}`;

      // File Size Format
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
      const sizeInGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
      const formattedSize = file.size > 1024 * 1024 * 1024 ? `${sizeInGB} GB` : `${sizeInMB} MB`;

      setTotalBytesFormatted(formattedSize);

      const metadata = {
        name: file.name,
        size: formattedSize,
        rawSizeBytes: file.size,
        duration: formattedDuration,
        durationSec,
        width,
        height,
        aspectRatio,
        qualityBadge,
        is4K,
        fps: 60,
        videoCodec: is4K ? 'H.265 / HEVC' : 'H.264 / AVC',
        audioCodec: 'AAC-LC (48 kHz)',
        bitrate: is4K ? '28.4 Mbps' : '9.5 Mbps',
        hdr: is4K ? 'HDR10' : 'SDR',
        colorSpace: is4K ? 'BT.2020' : 'BT.709',
      };

      setFileMetadata(metadata);
      if (!isManualTypeOverride) {
        setDetectedType(isShort ? 'short' : 'video');
      }

      // Generate Auto-Thumbnail Placeholders with real frame extraction
      generateAutoThumbnails(objectUrl, durationSec);

      // Start Upload Simulation
      startUploadSimulation(file.size);
    };

    tempVideo.onerror = () => {
      // Fallback metadata if metadata load fails
      const metadata = {
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        rawSizeBytes: file.size,
        duration: '02:45',
        durationSec: 165,
        width: 1920,
        height: 1080,
        aspectRatio: '16:9',
        qualityBadge: '1080p Full HD',
        is4K: false,
        fps: 60,
        videoCodec: 'H.264 / AVC',
        audioCodec: 'AAC',
        bitrate: '8.2 Mbps',
        hdr: 'SDR',
        colorSpace: 'BT.709',
      };
      setFileMetadata(metadata);
      setDetectedType('video');
      startUploadSimulation(file.size);
    };
  };

  // Helper to extract a single video frame at specific timestamp as base64 JPEG data URL
  const extractFrameAtTime = (videoUrl, timeSec) => {
    return new Promise((resolve) => {
      if (!videoUrl) {
        resolve(null);
        return;
      }
      const vid = document.createElement('video');
      // Only set crossOrigin for remote URLs, never for blob: or data: URLs
      if (!videoUrl.startsWith('blob:') && !videoUrl.startsWith('data:')) {
        vid.crossOrigin = 'anonymous';
      }
      vid.muted = true;
      vid.playsInline = true;
      vid.autoplay = false;
      let finished = false;

      const finish = (result) => {
        if (!finished) {
          finished = true;
          vid.onloadedmetadata = null;
          vid.onseeked = null;
          vid.onerror = null;
          vid.src = '';
          resolve(result);
        }
      };

      const captureFrame = () => {
        try {
          const w = vid.videoWidth || 640;
          const h = vid.videoHeight || 360;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(vid, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          if (dataUrl && dataUrl.length > 200) {
            finish(dataUrl);
            return true;
          }
        } catch (e) {
          console.warn('Canvas frame capture error:', e);
        }
        return false;
      };

      vid.onloadedmetadata = () => {
        const dur = vid.duration || 1;
        const target = Math.min(Math.max(timeSec, 0.2), Math.max(dur - 0.2, 0.2));
        vid.currentTime = target;
      };

      vid.onseeked = () => {
        if (!captureFrame()) {
          finish(null);
        }
      };

      vid.onerror = (e) => {
        console.warn('Video frame extraction load error:', e);
        finish(null);
      };

      vid.src = videoUrl;
      vid.load();

      // Fallback timeout after 3.5s
      setTimeout(() => {
        if (!finished) {
          if (!captureFrame()) {
            finish(null);
          }
        }
      }, 3500);
    });
  };

  // Generate 3 sample thumbnail snapshot cards with real canvas frame extraction
  const generateAutoThumbnails = async (url, durationSec) => {
    const dur = Math.max(durationSec || 5, 1);
    const t1 = Math.max(0.2, Math.min(dur * 0.15, dur - 0.2));
    const t2 = Math.max(0.5, Math.min(dur * 0.50, dur - 0.2));
    const t3 = Math.max(1.0, Math.min(dur * 0.85, dur - 0.2));

    const fmt = (t) => {
      const m = Math.floor(t / 60);
      const s = Math.floor(t % 60);
      return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    setAutoThumbnails([
      { id: 0, label: `Auto ${fmt(t1)}`, timeText: fmt(t1), src: null },
      { id: 1, label: `Auto ${fmt(t2)}`, timeText: fmt(t2), src: null },
      { id: 2, label: `Auto ${fmt(t3)}`, timeText: fmt(t3), src: null },
    ]);

    try {
      const [f1, f2, f3] = await Promise.all([
        extractFrameAtTime(url, t1),
        extractFrameAtTime(url, t2),
        extractFrameAtTime(url, t3),
      ]);

      setAutoThumbnails([
        { id: 0, label: `Auto ${fmt(t1)}`, timeText: fmt(t1), src: f1 },
        { id: 1, label: `Auto ${fmt(t2)}`, timeText: fmt(t2), src: f2 },
        { id: 2, label: `Auto ${fmt(t3)}`, timeText: fmt(t3), src: f3 },
      ]);
    } catch (err) {
      console.warn('Frame extraction failed:', err);
    }
  };

  // Upload Simulation
  const startUploadSimulation = (totalBytes) => {
    setUploadState('uploading');
    setUploadProgress(0);
    setProcessingStages({
      upload: 'processing',
      analyzed: 'pending',
      qualities: 'pending',
      audio: 'pending',
      thumbnails: 'pending',
      streaming: 'pending',
      checks: 'pending',
    });

    let currentPct = 0;
    if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);

    uploadTimerRef.current = setInterval(() => {
      currentPct += Math.floor(Math.random() * 8) + 4;
      if (currentPct >= 100) {
        currentPct = 100;
        clearInterval(uploadTimerRef.current);
        setUploadProgress(100);
        setUploadState('uploaded');
        setProcessingStages((prev) => ({ ...prev, upload: 'completed', analyzed: 'processing' }));
        startProcessingSimulation();
      } else {
        setUploadProgress(currentPct);
        const uploadedMB = ((totalBytes * (currentPct / 100)) / (1024 * 1024)).toFixed(1);
        const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
        setUploadedBytesFormatted(`${uploadedMB} MB`);
        setTotalBytesFormatted(`${totalMB} MB`);
        const remainingSec = Math.max(1, Math.ceil((100 - currentPct) / 3));
        setTimeRemaining(`About ${remainingSec} seconds remaining`);
      }
    }, 300);
  };

  const handlePauseUpload = () => {
    if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
    setUploadState('paused');
  };

  const handleResumeUpload = () => {
    if (selectedFile) {
      startUploadSimulation(selectedFile.size);
    }
  };

  // Processing & Transcoding Simulation
  const startProcessingSimulation = () => {
    setUploadState('processing');

    setTimeout(() => {
      setProcessingStages((prev) => ({ ...prev, analyzed: 'completed', qualities: 'processing' }));
    }, 600);

    setTimeout(() => {
      setRenditions([
        { res: '2160p (4K)', status: fileMetadata?.is4K ? 'Ready' : 'Not Supported' },
        { res: '1440p (QHD)', status: 'Ready' },
        { res: '1080p (FHD)', status: 'Ready' },
        { res: '720p (HD)', status: 'Ready' },
        { res: '480p (SD)', status: 'Ready' },
        { res: '360p (SD)', status: 'Ready' },
      ]);
      setProcessingStages((prev) => ({ ...prev, qualities: 'completed', audio: 'processing' }));
    }, 1400);

    setTimeout(() => {
      setProcessingStages((prev) => ({ ...prev, audio: 'completed', thumbnails: 'completed', streaming: 'completed', checks: 'processing' }));
    }, 2200);

    setTimeout(() => {
      setProcessingStages((prev) => ({ ...prev, checks: 'completed' }));
      setChecksStatus({ copyright: 'passed', contentSafety: 'passed', technical: 'passed' });
      setUploadState('ready');
    }, 3000);
  };

  // Drag & Drop Event Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  // Chapters Manager Handlers
  const handleAddChapter = () => {
    const newId = chapters.length + 1;
    setChapters([...chapters, { id: newId, timestamp: '02:00', title: `Chapter ${newId}` }]);
  };

  const handleDeleteChapter = (id) => {
    setChapters(chapters.filter((c) => c.id !== id));
  };

  // Album Handlers
  const handleCoverArtChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setAlbumCoverUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handlePlayboxBannerChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setAlbumPlayboxBannerUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAddAudioTracks = (files) => {
    if (!files || !files.length) return;
    Array.from(files).forEach((file, idx) => {
      const trackId = `trk_${Date.now()}_${idx}`;
      const objectUrl = URL.createObjectURL(file);
      const cleanTitle = file.name.replace(/\.[^/.]+$/, '').replace(/^[0-9]+[_\s.-]*/, '');

      const newTrack = {
        id: trackId,
        trackNumber: albumTracks.length + idx + 1,
        title: cleanTitle || `Track ${albumTracks.length + idx + 1}`,
        file,
        url: objectUrl,
        duration: '03:15',
        durationSec: 195,
        sizeFormatted: (file.size / (1024 * 1024)).toFixed(1) + ' MB',
      };

      const probeAudio = new Audio(objectUrl);
      probeAudio.addEventListener('loadedmetadata', () => {
        const sec = Math.round(probeAudio.duration) || 195;
        const mins = Math.floor(sec / 60);
        const remSec = sec % 60;
        const durStr = `${mins}:${remSec < 10 ? '0' : ''}${remSec}`;
        setAlbumTracks((prev) =>
          prev.map((t) => (t.id === trackId ? { ...t, duration: durStr, durationSec: sec } : t))
        );
      });

      setAlbumTracks((prev) => [...prev, newTrack]);
    });
  };

  const handleRemoveTrack = (trackId) => {
    if (playingTrackId === trackId) {
      if (albumAudioRef.current) albumAudioRef.current.pause();
      setPlayingTrackId(null);
    }
    setAlbumTracks((prev) =>
      prev.filter((t) => t.id !== trackId).map((t, i) => ({ ...t, trackNumber: i + 1 }))
    );
  };

  const handleMoveTrack = (index, direction) => {
    setAlbumTracks((prev) => {
      const copy = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= copy.length) return prev;
      const temp = copy[index];
      copy[index] = copy[targetIndex];
      copy[targetIndex] = temp;
      return copy.map((t, i) => ({ ...t, trackNumber: i + 1 }));
    });
  };

  const handleTrackTitleChange = (trackId, newTitle) => {
    setAlbumTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, title: newTitle } : t))
    );
  };

  const togglePlayTrack = (track) => {
    if (playingTrackId === track.id) {
      if (albumAudioRef.current) albumAudioRef.current.pause();
      setPlayingTrackId(null);
    } else {
      if (albumAudioRef.current) {
        albumAudioRef.current.src = track.url;
        albumAudioRef.current.currentTime = 0;
        setTrackCurrentTime(0);
        albumAudioRef.current.play().catch(() => {});
      }
      setPlayingTrackId(track.id);
    }
  };

  const handleSeekTrack = (ratio) => {
    if (albumAudioRef.current && trackDuration > 0) {
      const newTime = ratio * trackDuration;
      albumAudioRef.current.currentTime = newTime;
      setTrackCurrentTime(newTime);
    }
  };

  const handlePublishAlbum = async () => {
    localStorage.removeItem('speedotube_draft_album');
    setSavedAlbumDraftData(null);
    const activeAcc = getActiveAccount() || {};
    const finalAlbumData = {
      title: albumTitle || 'Untitled Album',
      artist: albumArtist || currentCreatorName,
      creator: currentCreatorName,
      genre: albumGenre,
      releaseYear: albumReleaseYear || new Date().getFullYear().toString(),
      description: albumDescription,
      coverUrl: albumCoverUrl || '',
      playboxBannerUrl: albumPlayboxBannerUrl || albumCoverUrl || '',
      visibility: albumVisibility,
      explicitAdvisory: albumAdvisory,
      channel_id: activeAcc.channelId || 38,
      user_id: activeAcc.userId || activeAcc.id || 132,
      tracks: albumTracks.map((t, idx) => ({
        id: t.id,
        trackNumber: idx + 1,
        title: t.title,
        duration: t.duration,
        durationSec: t.durationSec,
        url: t.url,
      })),
    };

    window.dispatchEvent(
      new CustomEvent('speedotube_show_toast', {
        detail: {
          message: 'Your Content has been Send to Approval to Monitor Team ..',
          icon: <MdInfoOutline size={20} style={{ color: '#3ea6ff' }} />,
        },
      })
    );

    await uploadChannelAlbum(finalAlbumData, activeAcc.channelId || 38, activeAcc.handle || activeAcc.name);
    window.dispatchEvent(new Event('speedotube_album_published'));
    if (onPublishSuccess) onPublishSuccess({ ...finalAlbumData, isAlbum: true });
    resetAlbumState();
    executeClose();
  };

  // Final Publish Handler
  const handlePublish = async () => {
    if (!editingItem) {
      localStorage.removeItem('speedotube_draft_video');
    }

    let chosenThumb =
      selectedThumbnail === 99
        ? customThumbnailUrl
        : (autoThumbnails && autoThumbnails[selectedThumbnail] ? autoThumbnails[selectedThumbnail].src : null) ||
          customThumbnailUrl ||
          (autoThumbnails && autoThumbnails[0] ? autoThumbnails[0].src : null) ||
          (editingItem ? editingItem.thumbnailUrl : null) ||
          null;

    if ((!chosenThumb || chosenThumb.startsWith('blob:')) && videoPreviewUrl && !editingItem) {
      try {
        const dur = fileMetadata?.durationSec || 5;
        const targetTime = selectedThumbnail === 1 ? dur * 0.5 : selectedThumbnail === 2 ? dur * 0.85 : Math.max(dur * 0.15, 0.2);
        const extracted = await extractFrameAtTime(videoPreviewUrl, targetTime);
        if (extracted) chosenThumb = extracted;
      } catch (e) {}
    }

    const finalData = {
      id: editingItem ? (editingItem.dbId || editingItem.id) : undefined,
      videoUrlId: editingItem?.videoUrlId,
      isEdit: Boolean(editingItem),
      title: videoTitle || 'Untitled Video',
      description: videoDescription,
      type: detectedType,
      isShort: detectedType === 'short',
      fileMetadata,
      visibility,
      schedule: visibility === 'schedule' ? { date: scheduleDate, time: scheduleTime, tz: scheduleTimezone } : null,
      category,
      tags: tagsInput,
      license,
      isForKids,
      thumbnailUrl: chosenThumb,
      file: selectedFile,
    };

    window.dispatchEvent(
      new CustomEvent('speedotube_show_toast', {
        detail: {
          message: 'Your Content has been Send to Approval to Monitor Team ..',
          icon: <MdInfoOutline size={20} style={{ color: '#3ea6ff' }} />,
        },
      })
    );

    if (onPublishSuccess) onPublishSuccess(finalData);
    onClose();
  };

  if (!isOpen && !isRendered) return null;

  return ReactDOM.createPortal(
    <div className={`spt-upload-modal-backdrop ${isClosing ? 'exiting' : ''}`}>
      <div className={`spt-upload-modal-card ${isClosing ? 'exiting' : ''}`}>
        {/* Draft Prompt Modal Overlay */}
        {showDraftPrompt && (uploadSection === 'album' ? savedAlbumDraftData : savedDraftData) && (
          <div className="spt-confirm-modal-overlay">
            <div className="spt-confirm-modal-card spt-draft-prompt-card">
              <div className={`spt-draft-badge-pill ${uploadSection === 'album' ? 'album-pill' : ''}`}>
                <MdCheckCircle size={15} />
                <span>{uploadSection === 'album' ? 'Saved Music Album Draft Found' : 'Saved Draft Found'}</span>
              </div>
              <h3 className="spt-confirm-title" style={{ marginTop: '12px' }}>
                {uploadSection === 'album' ? 'Continue with your Draft Music Album?' : 'Continue with your Draft Video?'}
              </h3>
              <p className="spt-confirm-desc">
                {uploadSection === 'album' ? (
                  <>
                    You have an unfinished music album draft (<strong>{savedAlbumDraftData.albumTitle || 'Untitled Album'}</strong>{savedAlbumDraftData.albumTracks?.length ? ` with ${savedAlbumDraftData.albumTracks.length} track${savedAlbumDraftData.albumTracks.length > 1 ? 's' : ''}` : ''}). Would you like to resume editing your album draft or start a new album?
                  </>
                ) : (
                  <>
                    You have an unfinished video draft (<strong>{savedDraftData.videoTitle || savedDraftData.fileName}</strong>). Would you like to resume editing your draft or start a new video upload?
                  </>
                )}
              </p>
              <div className="spt-confirm-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="spt-primary-pill-btn"
                  onClick={uploadSection === 'album' ? handleResumeAlbumDraft : handleResumeDraft}
                >
                  {uploadSection === 'album' ? 'Resume Album Draft' : 'Edit Draft Video'}
                </button>
                <button
                  type="button"
                  className="spt-subtle-btn danger"
                  onClick={uploadSection === 'album' ? handleDiscardAlbumDraftAndStartNew : handleDiscardDraftAndStartNew}
                >
                  {uploadSection === 'album' ? 'Discard & Start New' : 'Upload New Video'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation / Save Draft Confirmation Popup Modal Overlay */}
        {showCancelConfirm && (
          <div className="spt-confirm-modal-overlay">
            <div className="spt-confirm-modal-card">
              <h3 className="spt-confirm-title">
                {uploadSection === 'album' ? 'Close Album Upload?' : 'Close Video Upload?'}
              </h3>
              <p className="spt-confirm-desc">
                {uploadSection === 'album'
                  ? 'Your album draft progress is saved. Would you like to save your draft to continue later or discard it?'
                  : 'Your video draft progress is saved. Would you like to save your draft to continue later or discard it?'}
              </p>
              <div className="spt-confirm-actions">
                <button
                  type="button"
                  className="spt-primary-pill-btn"
                  onClick={handleSaveDraftAndClose}
                >
                  Save Draft & Close
                </button>
                <button
                  type="button"
                  className="spt-subtle-btn danger"
                  onClick={handleDiscardDraftAndClose}
                >
                  Discard Draft
                </button>
                <button
                  type="button"
                  className="spt-subtle-btn"
                  onClick={() => setShowCancelConfirm(false)}
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="spt-upload-header">
          <div className="spt-upload-header-left">
            <h2 className="spt-upload-title">
              {editingItem
                ? `Edit ${editingItem.isShort ? 'Short' : 'Video'}: ${editingItem.title || 'Details'}`
                : uploadSection === 'album'
                ? 'Upload Music Album'
                : detectedType === 'short'
                ? 'Upload Short'
                : 'Upload video'}
            </h2>
          </div>

          {!editingItem ? (
            <div className="spt-mode-switch-pills">
              <button
                type="button"
                className={`spt-mode-pill ${uploadSection === 'video' && detectedType !== 'short' ? 'active' : ''}`}
                onClick={() => switchSection('video', 'video')}
              >
                <MdOutlineVideocam size={17} />
                <span>Video</span>
              </button>
              <button
                type="button"
                className={`spt-mode-pill ${uploadSection === 'video' && detectedType === 'short' ? 'active' : ''}`}
                onClick={() => switchSection('video', 'short')}
              >
                <MdOutlineSlowMotionVideo size={17} />
                <span>Shorts</span>
              </button>
              <button
                type="button"
                className={`spt-mode-pill ${uploadSection === 'album' ? 'active' : ''}`}
                onClick={() => switchSection('album')}
              >
                <MdOutlineAlbum size={17} />
                <span>Music Album</span>
              </button>
            </div>
          ) : (
            <div className="spt-mode-switch-pills">
              <span className="spt-mode-pill active" style={{ cursor: 'default' }}>
                {editingItem.isShort ? <MdOutlineSlowMotionVideo size={17} /> : <MdOutlineVideocam size={17} />}
                <span>{editingItem.isShort ? 'Edit Short Details' : 'Edit Video Details'}</span>
              </span>
            </div>
          )}

          <div className="spt-upload-header-right">
            <span className={`spt-draft-badge ${isDraftSaved ? 'saved' : 'saving'}`}>
              <MdCheckCircle size={14} className="spt-draft-check-icon" />
              {editingItem
                ? 'Editing Live Content'
                : uploadSection === 'album'
                ? (isDraftSaved ? 'Album draft saved' : 'Saving draft...')
                : (isDraftSaved ? 'Saved as draft' : 'Saving draft...')}
            </span>
            <button className="spt-icon-btn" onClick={handleSmoothClose} title="Close upload modal">
              <MdClose size={22} />
            </button>
          </div>
        </div>

        {/* Stepper Progress Bar (Numbering Above Name) */}
        <div className="spt-upload-stepper">
          <div className="spt-stepper-inner">
            {uploadSection === 'album'
              ? [
                  { step: 1, label: 'Album Info' },
                  { step: 2, label: 'Tracklist' },
                  { step: 3, label: 'Visibility' },
                  { step: 4, label: 'Publish' },
                ].map((item) => {
                  const isActive = albumStep === item.step;
                  const isCompleted = albumStep > item.step;
                  return (
                    <React.Fragment key={item.step}>
                      <div
                        className={`spt-step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => navigateAlbumStep(item.step)}
                      >
                        <div className="spt-step-num">
                          {isCompleted ? <MdCheckCircle size={15} /> : item.step}
                        </div>
                        <span className="spt-step-label">{item.label}</span>
                      </div>
                      {item.step < 4 && (
                        <div className={`spt-step-line ${albumStep > item.step ? 'completed' : ''}`} />
                      )}
                    </React.Fragment>
                  );
                })
              : (editingItem
                  ? [
                      { step: 2, label: 'Details' },
                      { step: 3, label: 'Elements' },
                      { step: 4, label: 'Checks' },
                      { step: 5, label: 'Visibility' },
                    ]
                  : [
                      { step: 1, label: 'Upload' },
                      { step: 2, label: 'Details' },
                      { step: 3, label: 'Elements' },
                      { step: 4, label: 'Checks' },
                      { step: 5, label: 'Visibility' },
                    ]
                ).map((item, idx, arr) => {
                  const isActive = currentStep === item.step;
                  const isCompleted = currentStep > item.step;
                  return (
                    <React.Fragment key={item.step}>
                      <div
                        className={`spt-step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => {
                          if (selectedFile || editingItem) navigateVideoStep(item.step);
                        }}
                      >
                        <div className="spt-step-num">
                          {isCompleted ? <MdCheckCircle size={15} /> : (editingItem ? idx + 1 : item.step)}
                        </div>
                        <span className="spt-step-label">{item.label}</span>
                      </div>
                      {idx < arr.length - 1 && (
                        <div className={`spt-step-line ${currentStep > item.step ? 'completed' : ''}`} />
                      )}
                    </React.Fragment>
                  );
                })}
          </div>
        </div>

        {/* Hidden Audio Element for Album Track Preview Playback */}
        <audio
          ref={albumAudioRef}
          onTimeUpdate={(e) => setTrackCurrentTime(e.target.currentTime)}
          onLoadedMetadata={(e) => setTrackDuration(e.target.duration || 0)}
          onEnded={() => {
            setPlayingTrackId(null);
            setTrackCurrentTime(0);
          }}
        />

        {/* Modal Body (Adapts based on currentStep & uploadSection) */}
        <div className="spt-upload-body">
          <div
            key={uploadSection === 'album' ? 'album' : detectedType}
            className="spt-section-pane-anim"
          >
            {uploadSection === 'album' ? (
              <AlbumUploadWorkflow
                albumStep={albumStep}
                setAlbumStep={setAlbumStep}
                transitionDirection={transitionDirection}
                albumTitle={albumTitle}
                setAlbumTitle={setAlbumTitle}
                albumArtist={albumArtist}
                setAlbumArtist={setAlbumArtist}
                albumGenre={albumGenre}
                setAlbumGenre={setAlbumGenre}
                albumReleaseYear={albumReleaseYear}
                setAlbumReleaseYear={setAlbumReleaseYear}
                albumDescription={albumDescription}
                setAlbumDescription={setAlbumDescription}
                albumCoverUrl={albumCoverUrl}
                albumCoverInputRef={albumCoverInputRef}
                handleCoverArtChange={handleCoverArtChange}
                albumPlayboxBannerUrl={albumPlayboxBannerUrl}
                albumPlayboxBannerInputRef={albumPlayboxBannerInputRef}
                handlePlayboxBannerChange={handlePlayboxBannerChange}
                albumTracks={albumTracks}
                albumTrackInputRef={albumTrackInputRef}
                handleAddAudioTracks={handleAddAudioTracks}
                handleRemoveTrack={handleRemoveTrack}
                handleMoveTrack={handleMoveTrack}
                handleTrackTitleChange={handleTrackTitleChange}
                playingTrackId={playingTrackId}
                togglePlayTrack={togglePlayTrack}
                trackCurrentTime={trackCurrentTime}
                trackDuration={trackDuration}
                handleSeekTrack={handleSeekTrack}
                albumVisibility={albumVisibility}
                setAlbumVisibility={setAlbumVisibility}
                albumAdvisory={albumAdvisory}
                setAlbumAdvisory={setAlbumAdvisory}
              />
          ) : (!selectedFile && !editingItem) ? (
            /* Focused Single Upload Dropzone */
            <div className="spt-dropzone-center-wrapper">
              <div
                className={`spt-dropzone ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="spt-dropzone-icon-box" style={{ marginBottom: '20px' }}>
                  {detectedType === 'short' ? (
                    <MdOutlineSlowMotionVideo size={56} className="spt-short-icon" />
                  ) : (
                    <MdCloudUpload size={56} className="spt-cloud-icon" />
                  )}
                </div>

                <h3 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
                  {detectedType === 'short' ? 'Upload Short' : 'Upload video'}
                </h3>

                <p className="spt-dropzone-sub" style={{ maxWidth: '400px', margin: '0 0 24px 0', fontSize: '14px', lineHeight: '1.5' }}>
                  {detectedType === 'short'
                    ? 'Drag and drop your vertical short video file here (up to 60s), or choose a file from your device.'
                    : 'Drag and drop your video file here, or choose a file from your device.'}
                </p>

                <button
                  className="spt-primary-pill-btn"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{ padding: '12px 28px', fontSize: '14px' }}
                >
                  {detectedType === 'short' ? 'Select Short File' : 'Select Video File'}
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
                />

                <div className="spt-dropzone-footer-hints" style={{ marginTop: '28px' }}>
                  <span className="spt-format-hint">Formats: MP4, MOV, WebM</span>
                  <span className="spt-size-limit">• Maximum size: 16 GB</span>
                </div>
              </div>
            </div>
          ) : (
            /* File Selected: 2-Column Layout (Left: Step Main Content, Right: Persistent Video Preview Card) */
            <div className="spt-two-col-layout">
              {/* Left Column: Multi-Step Active View */}
              <div className="spt-main-step-col">
                {/* STEP 1: UPLOAD & FILE ANALYSIS */}
                {currentStep === 1 && (
                  <div className="spt-uploaded-analysis-view">
                    {/* Top Status & Type Banner */}
                    <div className="spt-type-detection-banner">
                      <div className="spt-type-chip-wrapper">
                        <span className={`spt-type-chip ${detectedType}`}>
                          {detectedType === 'short' ? <MdOutlineSlowMotionVideo size={16} /> : <MdOutlineVideocam size={16} />}
                          {detectedType === 'short' ? 'Detected as Short (9:16 Vertical)' : 'Detected as Video (16:9 Landscape)'}
                        </span>
                      </div>

                      {/* Manual Classification Override */}
                      <div className="spt-type-toggle">
                        <span className="spt-toggle-label">Format:</span>
                        <div className="spt-toggle-btn-group">
                          <button
                            type="button"
                            className={`spt-toggle-btn ${detectedType === 'video' ? 'active' : ''}`}
                            onClick={() => { setDetectedType('video'); setIsManualTypeOverride(true); }}
                          >
                            Long Video
                          </button>
                          <button
                            type="button"
                            className={`spt-toggle-btn ${detectedType === 'short' ? 'active' : ''}`}
                            onClick={() => { setDetectedType('short'); setIsManualTypeOverride(true); }}
                          >
                            Short
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Resolution Quality Badge Card */}
                    {fileMetadata && (
                      <div className={`spt-quality-detection-card ${fileMetadata.is4K ? 'is-4k' : ''}`}>
                        <div className="spt-quality-badge-box">
                          <span className="spt-resolution-badge">{fileMetadata.qualityBadge}</span>
                        </div>
                        <div className="spt-quality-info-text">
                          <h4>{fileMetadata.is4K ? '4K Ultra HD Source Detected' : `${fileMetadata.qualityBadge} Detected`}</h4>
                          <p>
                            {fileMetadata.is4K
                              ? `Your original video is ${fileMetadata.width} × ${fileMetadata.height}. SpeedoTube will process multi-pass renditions up to 4K 60FPS.`
                              : `Source resolution is ${fileMetadata.width} × ${fileMetadata.height}. Genuine 4K playback cannot be generated from a 1080p/720p source.`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Upload Progress & Processing Status Card */}
                    <div className="spt-progress-card">
                      <div className="spt-progress-header">
                        <div className="spt-progress-file-info">
                          <span className="spt-file-name">{fileMetadata?.name}</span>
                          <span className="spt-file-meta">{fileMetadata?.size} • {fileMetadata?.duration}</span>
                        </div>
                        <span className="spt-progress-pct">{uploadProgress}%</span>
                      </div>

                      {/* Animated Progress Bar */}
                      <div className="spt-progress-bar-bg">
                        <div
                          className={`spt-progress-bar-fill ${uploadState === 'paused' ? 'paused' : ''}`}
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>

                      <div className="spt-progress-footer">
                        <span className="spt-speed-text">
                          {uploadState === 'uploading' && `${uploadedBytesFormatted} / ${totalBytesFormatted} • ${uploadSpeed}`}
                          {uploadState === 'paused' && 'Upload paused'}
                          {(uploadState === 'uploaded' || uploadState === 'processing') && 'Upload completed • Processing video...'}
                          {uploadState === 'ready' && 'Upload completed'}
                        </span>
                        <span className={`spt-remaining-time ${uploadState === 'ready' ? 'finished-green' : ''}`}>
                          {uploadState === 'uploading' && timeRemaining}
                          {uploadState === 'paused' && 'Your upload can continue from where it stopped.'}
                          {uploadState === 'ready' && 'All processing finished!'}
                        </span>
                      </div>

                      {/* Progress Control Action Buttons */}
                      <div className="spt-progress-actions">
                        {uploadState === 'uploading' && (
                          <button className="spt-subtle-btn" onClick={handlePauseUpload}>
                            <MdPause size={16} /> Pause upload
                          </button>
                        )}
                        {uploadState === 'paused' && (
                          <button className="spt-primary-pill-btn sm" onClick={handleResumeUpload}>
                            <MdPlayArrow size={16} /> Resume upload
                          </button>
                        )}
                        <button
                          className="spt-subtle-btn danger"
                          onClick={() => setShowCancelConfirm(true)}
                        >
                          Cancel upload
                        </button>
                      </div>
                    </div>

                    {/* Multistage Processing Checklist */}
                    <div className="spt-processing-checklist">
                      <h4 className="spt-section-sub-title">Transcoding & Processing Stages</h4>
                      <div className="spt-checklist-grid">
                        <div className="spt-check-item">
                          {processingStages.upload === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Upload completed</span>
                        </div>
                        <div className="spt-check-item">
                          {processingStages.analyzed === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Source metadata analyzed</span>
                        </div>
                        <div className="spt-check-item">
                          {processingStages.qualities === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Generating renditions (4K, 1440p, 1080p, 720p)</span>
                        </div>
                        <div className="spt-check-item">
                          {processingStages.audio === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Processing multi-channel audio</span>
                        </div>
                        <div className="spt-check-item">
                          {processingStages.thumbnails === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Generating auto-thumbnails</span>
                        </div>
                        <div className="spt-check-item">
                          {processingStages.streaming === 'completed' ? <MdCheckCircle color="#10b981" size={18} /> : <div className="spt-spinner-sm" />}
                          <span>Preparing HLS / DASH adaptive streaming</span>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Technical Information Accordion */}
                    <div className={`spt-technical-accordion ${isTechOpen ? 'open' : ''}`}>
                      <button
                        type="button"
                        className="spt-tech-summary"
                        onClick={() => setIsTechOpen(!isTechOpen)}
                        aria-expanded={isTechOpen}
                      >
                        <span>Technical information & Codecs</span>
                        <MdExpandMore size={22} className="spt-tech-chevron" />
                      </button>
                      <div className="spt-tech-content-wrapper">
                        <div className="spt-tech-content-inner">
                          {fileMetadata && (
                            <div className="spt-tech-specs-grid">
                              <div className="spt-spec-card">
                                <span className="spec-label">Resolution</span>
                                <span className="spec-val">{fileMetadata.width} × {fileMetadata.height}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Frame rate</span>
                                <span className="spec-val">{fileMetadata.fps} FPS</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Video Codec</span>
                                <span className="spec-val">{fileMetadata.videoCodec}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Audio Codec</span>
                                <span className="spec-val">{fileMetadata.audioCodec}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Bitrate</span>
                                <span className="spec-val">{fileMetadata.bitrate}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">HDR Status</span>
                                <span className="spec-val">{fileMetadata.hdr}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Color Space</span>
                                <span className="spec-val">{fileMetadata.colorSpace}</span>
                              </div>
                              <div className="spt-spec-card">
                                <span className="spec-label">Aspect Ratio</span>
                                <span className="spec-val">{fileMetadata.aspectRatio}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2: DETAILS (METADATA, THUMBNAIL, AUDIENCE, ADVANCED) */}
                {currentStep === 2 && (
                  <div className="spt-form-col">
                {/* Title */}
                <div className="spt-field-group">
                  <div className="spt-label-row">
                    <label htmlFor="videoTitle">Title (required)</label>
                    <span className="spt-char-counter">{videoTitle.length} / 100</span>
                  </div>
                  <input
                    type="text"
                    id="videoTitle"
                    className="spt-input-text"
                    placeholder="Add a title that describes your video"
                    value={videoTitle}
                    onChange={(e) => setVideoTitle(e.target.value.slice(0, 100))}
                  />
                </div>

                {/* Description */}
                <div className="spt-field-group">
                  <div className="spt-label-row">
                    <label htmlFor="videoDesc">Description</label>
                    <span className="spt-char-counter">{videoDescription.length} / 5000</span>
                  </div>
                  <textarea
                    id="videoDesc"
                    className="spt-textarea"
                    rows={4}
                    placeholder="Tell viewers about your video (links, timestamps, social handles)"
                    value={videoDescription}
                    onChange={(e) => setVideoDescription(e.target.value.slice(0, 5000))}
                  />
                </div>

                {/* Short Hashtags if Short */}
                {detectedType === 'short' && (
                  <div className="spt-field-group">
                    <label htmlFor="shortHashtags">Short Hashtags</label>
                    <input
                      type="text"
                      id="shortHashtags"
                      className="spt-input-text"
                      value={shortHashtags}
                      onChange={(e) => setShortHashtags(e.target.value)}
                    />
                  </div>
                )}

                {/* Thumbnail Selector */}
                <div className="spt-field-group">
                  <label>Thumbnail</label>
                  <p className="spt-field-desc">Select or upload a picture that shows what is in your video.</p>

                  <div className={`spt-thumbnail-picker-grid ${detectedType === 'short' ? 'is-short' : ''}`}>
                    {/* Upload Custom Thumbnail Button */}
                    <div
                      className={`spt-thumb-box upload-box ${detectedType === 'short' ? 'is-short' : ''}`}
                      onClick={() => thumbnailInputRef.current && thumbnailInputRef.current.click()}
                    >
                      <MdCloudUpload size={22} className="spt-upload-thumb-icon" />
                      <span className="spt-upload-thumb-text">Upload file</span>
                      <span className="spt-upload-thumb-sub">Auto or custom</span>
                    </div>

                    <input
                      type="file"
                      ref={thumbnailInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          const reader = new FileReader();
                          reader.onload = (uploadEvt) => {
                            const base64Data = uploadEvt.target.result;
                            setCustomThumbnailUrl(base64Data);
                            setSelectedThumbnail(99);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />

                    {/* Custom Uploaded Thumb Card if present */}
                    {customThumbnailUrl && (
                      <div
                        className={`spt-thumb-box ${detectedType === 'short' ? 'is-short' : ''} ${selectedThumbnail === 99 ? 'selected' : ''}`}
                        onClick={() => setSelectedThumbnail(99)}
                      >
                        <img src={customThumbnailUrl} alt="Custom Thumbnail" className="spt-thumb-frame-preview" />
                        <span className="spt-thumb-time-badge">Custom</span>
                        {selectedThumbnail === 99 && (
                          <div className="spt-thumb-selected-overlay">
                            <MdCheckCircle size={15} className="spt-thumb-check" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Auto Generated Thumbnails */}
                    {autoThumbnails.map((thumb) => (
                      <div
                        key={thumb.id}
                        className={`spt-thumb-box ${detectedType === 'short' ? 'is-short' : ''} ${selectedThumbnail === thumb.id ? 'selected' : ''}`}
                        onClick={() => setSelectedThumbnail(thumb.id)}
                      >
                        {thumb.src && thumb.src.startsWith('data:') ? (
                          <img src={thumb.src} alt={thumb.label} className="spt-thumb-frame-preview" />
                        ) : (
                          <div className="spt-thumb-placeholder">
                            <span>{thumb.timeText || thumb.label}</span>
                          </div>
                        )}
                        <span className="spt-thumb-time-badge">{thumb.timeText || thumb.label}</span>
                        {selectedThumbnail === thumb.id && (
                          <div className="spt-thumb-selected-overlay">
                            <MdCheckCircle size={15} className="spt-thumb-check" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Audience COPPA Section */}
                <div className="spt-field-group spt-coppa-card">
                  <div className="spt-coppa-header">
                    <h4>Audience & Content Classification</h4>
                    <div className="spt-info-tooltip-wrapper" data-yt-tooltip="Required by law for content classification & child protection">
                      <MdInfoOutline size={18} className="spt-info-icon" />
                    </div>
                  </div>
                  <p className="spt-coppa-question">
                    Regardless of your location, you're legally required to comply with the Children's Online Privacy Protection Act (COPPA) and other laws. Select the audience classification for this {detectedType === 'short' ? 'short' : 'video'}:
                  </p>

                  <div className="spt-audience-cards-grid">
                    <div
                      className={`spt-album-vis-card spt-audience-card ${isForKids === 'yes' ? 'active' : ''}`}
                      onClick={() => setIsForKids('yes')}
                    >
                      <MdChildCare size={22} className="spt-album-vis-icon" />
                      <div>
                        <strong className="spt-album-card-title">Yes, it's made for kids</strong>
                        <p className="spt-album-card-desc">Features content designed specifically for children</p>
                      </div>
                    </div>

                    <div
                      className={`spt-album-vis-card spt-audience-card ${isForKids === 'all' ? 'active' : ''}`}
                      onClick={() => setIsForKids('all')}
                    >
                      <MdFamilyRestroom size={22} className="spt-album-vis-icon" />
                      <div>
                        <strong className="spt-album-card-title">General Audience (All Ages)</strong>
                        <p className="spt-album-card-desc">Suitable for kids as well as other viewers (Family-friendly)</p>
                      </div>
                    </div>

                    <div
                      className={`spt-album-vis-card spt-audience-card ${isForKids === 'no' ? 'active' : ''}`}
                      onClick={() => setIsForKids('no')}
                    >
                      <Icon18Plus size={22} className="spt-album-vis-icon" />
                      <div>
                        <strong className="spt-album-card-title">No, not made for kids</strong>
                        <p className="spt-album-card-desc">Contains mature language, themes, or adult content</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Advanced Details Accordion */}
                <div className={`spt-advanced-accordion ${showAdvancedDetails ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="spt-advanced-summary-btn"
                    onClick={() => setShowAdvancedDetails(!showAdvancedDetails)}
                    aria-expanded={showAdvancedDetails}
                  >
                    <div className="spt-summary-title-wrapper">
                      <span className="spt-summary-title">Advanced details</span>
                      <span className="spt-summary-sub">(Category, Tags, License, Comments)</span>
                    </div>
                    <MdExpandMore className={`spt-chevron-icon ${showAdvancedDetails ? 'rotated' : ''}`} size={22} />
                  </button>

                  <div className={`spt-advanced-form-collapse ${showAdvancedDetails ? 'open' : ''}`}>
                    <div className="spt-advanced-form-inner">
                      {/* Category */}
                      <div className="spt-field-group">
                        <label>Category</label>
                        <CustomSelect
                          options={[
                            'Gaming',
                            'Technology & Science',
                            'Music & Audio',
                            'Entertainment',
                            'Education',
                            'News & Politics',
                            'Sports',
                          ]}
                          value={category}
                          onChange={setCategory}
                        />
                      </div>

                      {/* Language */}
                      <div className="spt-field-group">
                        <label>Video Language</label>
                        <CustomSelect
                          options={['English', 'Spanish', 'Hindi', 'French', 'German', 'Japanese']}
                          value={language}
                          onChange={setLanguage}
                        />
                      </div>

                      {/* Tags */}
                      <div className="spt-field-group">
                        <label htmlFor="tagsInput">Tags</label>
                        <input
                          type="text"
                          id="tagsInput"
                          className="spt-input-text"
                          placeholder="Add tags separated by commas"
                          value={tagsInput}
                          onChange={(e) => setTagsInput(e.target.value)}
                        />
                      </div>

                      {/* License */}
                      <div className="spt-field-group">
                        <label>License</label>
                        <CustomSelect
                          options={['SpeedoTube Standard License', 'Creative Commons - Attribution']}
                          value={license}
                          onChange={setLicense}
                        />
                      </div>

                      {/* Toggles Grid */}
                      <div className="spt-toggles-grid">
                        <label className="spt-toggle-row">
                          <input
                            type="checkbox"
                            checked={allowEmbedding}
                            onChange={(e) => setAllowEmbedding(e.target.checked)}
                          />
                          <span>Allow embedding on external sites</span>
                        </label>

                        <label className="spt-toggle-row">
                          <input
                            type="checkbox"
                            checked={showLikeCount}
                            onChange={(e) => setShowLikeCount(e.target.checked)}
                          />
                          <span>Show how many viewers like this video</span>
                        </label>

                        <label className="spt-toggle-row">
                          <input
                            type="checkbox"
                            checked={paidPromotion}
                            onChange={(e) => setPaidPromotion(e.target.checked)}
                          />
                          <span>My video contains paid promotion (sponsorship)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          )}

          {/* ==================================================================== */}
          {/* STEP 3: ELEMENTS (SUBTITLES, AUDIO TRACKS, CHAPTERS)                 */}
          {/* ==================================================================== */}
          {currentStep === 3 && (
            <div className="spt-step-container">
              <div className="spt-elements-grid">
                {/* Card 1: Subtitles & Captions */}
                <div className="spt-element-card">
                  <div className="spt-element-content">
                    <h4>Subtitles & Captions</h4>
                    <p>Reach a broader global audience by adding captions in multiple languages.</p>
                    <div className="spt-element-actions">
                      <button className="spt-subtle-btn">Upload SRT / VTT file</button>
                      <button className="spt-subtle-btn">Auto-generate captions</button>
                    </div>
                  </div>
                </div>

                {/* Card 2: Multi-Language Audio Tracks (OTT feature) */}
                <div className="spt-element-card">
                  <div className="spt-element-content">
                    <h4>Multi-Language Audio Tracks</h4>
                    <p>Add alternate audio dubs for global SpeedoTube OTT streaming.</p>

                    <div className="spt-audio-tracks-list">
                      {audioTracksList.map((track) => (
                        <div key={track.id} className="spt-audio-track-item">
                          <span>{track.lang} — {track.name}</span>
                          <span className="spt-status-pill">{track.status}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      className="spt-subtle-btn"
                      onClick={() => {
                        const newId = audioTracksList.length + 1;
                        setAudioTracksList([...audioTracksList, { id: newId, lang: 'Spanish', name: 'Spanish Audio Dub', status: 'Uploaded' }]);
                      }}
                    >
                      <MdAdd size={16} /> Add audio track
                    </button>
                  </div>
                </div>

                {/* Card 3: Video Chapters (Only for regular long videos, not for Shorts) */}
                {detectedType !== 'short' && (
                  <div className="spt-element-card">
                    <div className="spt-element-content">
                      <h4>Video Chapters</h4>
                      <p>Create timestamps to help viewers navigate key sections of your video.</p>

                      <div className="spt-chapters-list">
                        {chapters.map((ch) => (
                          <div key={ch.id} className="spt-chapter-row">
                            <input
                              type="text"
                              className="spt-input-ts"
                              value={ch.timestamp}
                              onChange={(e) => {
                                const val = e.target.value;
                                setChapters(chapters.map((c) => (c.id === ch.id ? { ...c, timestamp: val } : c)));
                              }}
                            />
                            <input
                              type="text"
                              className="spt-input-text"
                              value={ch.title}
                              onChange={(e) => {
                                const val = e.target.value;
                                setChapters(chapters.map((c) => (c.id === ch.id ? { ...c, title: val } : c)));
                              }}
                            />
                            <button className="spt-icon-btn danger" onClick={() => handleDeleteChapter(ch.id)}>
                              <MdDeleteOutline size={18} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <button className="spt-subtle-btn" onClick={handleAddChapter}>
                        <MdAdd size={16} /> Add chapter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================================================================== */}
          {/* STEP 4: CHECKS (COPYRIGHT, CONTENT SAFETY, TECHNICAL)                 */}
          {/* ==================================================================== */}
          {currentStep === 4 && (
            <div className="spt-step-container">
              <div className="spt-checks-header">
                <h3>Pre-Publication Validation & Checks</h3>
                <p>We analyze your video for technical compatibility, copyright, and safety guidelines before publishing.</p>
              </div>

              <div className="spt-checks-cards-list">
                {/* Check 1: Copyright Scan */}
                <div className="spt-check-card">
                  <div className="spt-check-card-left">
                    <div>
                      <h4>Copyright Check</h4>
                      <p>Scanned against SpeedoTube Content ID database. No copyrighted audio or visual matches detected.</p>
                    </div>
                  </div>
                  <span className="spt-check-status-badge passed">✓ Passed</span>
                </div>

                {/* Check 2: Content Safety Review */}
                <div className="spt-check-card">
                  <div className="spt-check-card-left">
                    <div>
                      <h4>Content Safety & Guidelines</h4>
                      <p>Scanned for advertiser friendliness, graphic material, and community guidelines.</p>
                    </div>
                  </div>
                  <span className="spt-check-status-badge passed">✓ Passed</span>
                </div>

                {/* Check 3: Technical Validation */}
                <div className="spt-check-card">
                  <div className="spt-check-card-left">
                    <div>
                      <h4>Technical Validation & Adaptive Streaming</h4>
                      <p>
                        {fileMetadata?.is4K
                          ? '4K UHD 60 FPS source validated • HLS/DASH adaptive renditions generated.'
                          : '1080p FHD source validated • HLS/DASH adaptive renditions ready.'}
                      </p>
                    </div>
                  </div>
                  <span className="spt-check-status-badge passed">✓ Ready</span>
                </div>
              </div>
            </div>
          )}

          {/* ==================================================================== */}
          {/* STEP 5: VISIBILITY & SCHEDULING                                     */}
          {/* ==================================================================== */}
          {currentStep === 5 && (
            <div className="spt-step-container">
              {/* Left Column: Visibility Options */}
              <div className="spt-visibility-col">
                <h3>Choose visibility</h3>
                <p className="spt-sub-heading">Select when and who can watch your video on SpeedoTube.</p>

                <div className="spt-visibility-cards-grid">
                  {/* Public */}
                  <div
                    className={`spt-vis-card ${visibility === 'public' ? 'selected' : ''}`}
                    onClick={() => setVisibility('public')}
                  >
                    <div className="spt-vis-header">
                      <MdOutlinePublic size={24} color="#00d2ff" />
                      <strong>Public</strong>
                    </div>
                    <p>Anyone on SpeedoTube can search for and watch your video immediately.</p>
                  </div>

                  {/* Unlisted */}
                  <div
                    className={`spt-vis-card ${visibility === 'unlisted' ? 'selected' : ''}`}
                    onClick={() => setVisibility('unlisted')}
                  >
                    <div className="spt-vis-header">
                      <MdOutlineLink size={24} color="#00d2ff" />
                      <strong>Unlisted</strong>
                    </div>
                    <p>Anyone with the video link can watch your video.</p>
                  </div>

                  {/* Private */}
                  <div
                    className={`spt-vis-card ${visibility === 'private' ? 'selected' : ''}`}
                    onClick={() => setVisibility('private')}
                  >
                    <div className="spt-vis-header">
                      <MdOutlineLock size={24} color="#00d2ff" />
                      <strong>Private</strong>
                    </div>
                    <p>Only you and people you explicitly invite can watch this video.</p>
                  </div>

                  {/* Schedule */}
                  <div
                    className={`spt-vis-card ${visibility === 'schedule' ? 'selected' : ''}`}
                    onClick={() => setVisibility('schedule')}
                  >
                    <div className="spt-vis-header">
                      <MdOutlineSchedule size={24} color="#00d2ff" />
                      <strong>Schedule</strong>
                    </div>
                    <p>Select a future date and time to automatically publish your video.</p>

                    {visibility === 'schedule' && (
                      <div className="spt-schedule-picker-box" onClick={(e) => e.stopPropagation()}>
                        <div className="spt-picker-field">
                          <label>Date</label>
                          <CustomDatePicker
                            value={scheduleDate}
                            onChange={setScheduleDate}
                          />
                        </div>
                        <div className="spt-picker-field">
                          <label>Time</label>
                          <CustomTimePicker
                            value={scheduleTime}
                            onChange={setScheduleTime}
                          />
                        </div>
                        <div className="spt-picker-field">
                          <label>Timezone</label>
                          <CustomSelect
                            options={['IST (UTC+5:30)', 'EST (UTC-5:00)', 'PST (UTC-8:00)', 'UTC (UTC+0:00)']}
                            value={scheduleTimezone}
                            onChange={setScheduleTimezone}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

            {/* Persistent Right Column Video Preview Card (Fixed across ALL steps) */}
            <div className="spt-preview-side-col">
              <div className="spt-side-preview-card">
                <div className={`spt-video-preview-card ${detectedType === 'short' ? 'short-preview' : ''}`}>
                  {videoPreviewUrl ? (
                    <video
                      src={videoPreviewUrl}
                      controls
                      controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
                      disablePictureInPicture
                      muted
                      className="spt-preview-player"
                    />
                  ) : (customThumbnailUrl || editingItem?.thumbnailUrl) ? (
                    <img
                      src={customThumbnailUrl || editingItem?.thumbnailUrl}
                      alt="Thumbnail preview"
                      className="spt-preview-player"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="spt-preview-placeholder">
                      <MdOutlineVideocam size={48} color="#64748b" />
                    </div>
                  )}
                  <span className="spt-preview-duration">{fileMetadata?.duration || '00:00'}</span>
                  <span className="spt-preview-quality-tag">{fileMetadata?.qualityBadge || '1080p'}</span>
                </div>

                <div className="spt-preview-meta-summary">
                  <div className="spt-summary-line">
                    <span className="label">Video link</span>
                    <div className="spt-link-copy-row">
                      {(() => {
                        const vidTarget = editingItem ? (editingItem.videoUrlId || editingItem.dbId || editingItem.id) : (fileMetadata?.name ? fileMetadata.name.slice(0, 8) : 'demo');
                        const fullLink = detectedType === 'short'
                          ? `https://speedotube.com/shorts/${vidTarget}`
                          : `https://speedotube.com/watch?v=${vidTarget}`;
                        return (
                          <>
                            <span className="val link-url">{fullLink}</span>
                            <button
                              type="button"
                              className="spt-icon-copy-btn"
                              onClick={() => {
                                if (navigator.clipboard) navigator.clipboard.writeText(fullLink);
                              }}
                              title="Copy video link"
                            >
                              <MdOutlineLink size={18} />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="spt-summary-line">
                    <span className="label">{editingItem ? 'Title' : 'Filename'}</span>
                    <span className="val filename">{editingItem?.title || fileMetadata?.name || selectedFile?.name}</span>
                  </div>
                  <div className="spt-summary-line">
                    <span className="label">Format</span>
                    <span className="val">{detectedType === 'short' ? 'Short Video (9:16)' : 'Long Video (16:9)'}</span>
                  </div>
                </div>

                {detectedType === 'short' && (
                  <div className="spt-short-highlight-box">
                    <MdOutlineSlowMotionVideo size={20} className="spt-short-active-icon" />
                    <div>
                      <strong>Short Format Active</strong>
                      <p>Vertical 9:16 playback format & SpeedoShorts feed distribution.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

        {/* Footer Navigation Action Bar */}
        <div className="spt-upload-footer">
          <div className="spt-footer-left">
            {uploadSection === 'album' ? (
              albumStep > 1 && (
                <button
                  type="button"
                  className="spt-subtle-btn"
                  onClick={() => navigateAlbumStep(albumStep - 1)}
                >
                  Back
                </button>
              )
            ) : (
              currentStep > (editingItem ? 2 : 1) && (
                <button
                  type="button"
                  className="spt-subtle-btn"
                  onClick={() => navigateVideoStep(currentStep - 1)}
                >
                  Back
                </button>
              )
            )}
          </div>

          <div className="spt-footer-right">
            {uploadSection === 'album' ? (
              albumStep < 4 ? (
                <button
                  type="button"
                  className="spt-primary-pill-btn"
                  disabled={albumStep === 1 && !albumTitle.trim()}
                  onClick={() => navigateAlbumStep(albumStep + 1)}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="spt-primary-pill-btn publish"
                  onClick={handlePublishAlbum}
                >
                  Publish Album
                </button>
              )
            ) : (
              currentStep < 5 ? (
                <button
                  type="button"
                  className="spt-primary-pill-btn"
                  disabled={!selectedFile && !editingItem}
                  onClick={() => navigateVideoStep(currentStep + 1)}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="spt-primary-pill-btn publish"
                  onClick={handlePublish}
                >
                  {editingItem
                    ? 'Save & Submit for Approval'
                    : visibility === 'schedule'
                    ? 'Schedule'
                    : detectedType === 'short'
                    ? 'Publish Short'
                    : 'Publish Video'}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
